import type { Patient } from "../contexts/types";

export type VoicePatientDraftField =
  | "name"
  | "age"
  | "sex"
  | "chiefComplaint"
  | "historyOfPresentIllness"
  | "painLocation"
  | "symptomDuration"
  | "traumaMechanism"
  | "functionalLimitation"
  | "physicalExam"
  | "suspectedDiagnosis"
  | "initialPlan"
  | "notes";

export type VoicePatientExtractionJson = {
  name: string | null;
  age: number | null;
  sex: "M" | "F" | null;
  chiefComplaint: string | null;
  historyOfPresentIllness: string | null;
  painLocation: string | null;
  symptomDuration: string | null;
  traumaMechanism: string | null;
  functionalLimitation: string | null;
  physicalExam: string | null;
  suspectedDiagnosis: string | null;
  initialPlan: string | null;
  notes: string | null;
};

export type VoicePatientParserCorrection = {
  field: VoicePatientDraftField;
  original: string;
  corrected: string | null;
  reason: string;
};

export type VoicePatientDraft = {
  name?: string;
  age?: string;
  sex?: "M" | "F";
  chiefComplaint?: string;
  historyOfPresentIllness?: string;
  painLocation?: string;
  symptomDuration?: string;
  traumaMechanism?: string;
  functionalLimitation?: string;
  physicalExam?: string;
  suspectedDiagnosis?: string;
  initialPlan?: string;
  notes?: string;
  transcript?: string;
  structuredJson?: VoicePatientExtractionJson;
  confidence?: Partial<Record<VoicePatientDraftField, number>>;
  lowConfidenceFields?: VoicePatientDraftField[];
  autofilledFields?: VoicePatientDraftField[];
  parserCorrections?: VoicePatientParserCorrection[];
};

type TranscriptSource = {
  fallbackTranscript?: string;
  language?: string;
  useMock?: boolean;
};

type FieldCandidate<T extends string | number = string> = {
  value: T | null;
  confidence: number;
};

const draftFieldKeys: VoicePatientDraftField[] = [
  "name",
  "age",
  "sex",
  "chiefComplaint",
  "historyOfPresentIllness",
  "painLocation",
  "symptomDuration",
  "traumaMechanism",
  "functionalLimitation",
  "physicalExam",
  "suspectedDiagnosis",
  "initialPlan",
  "notes",
];

export const VOICE_PATIENT_EXTRACTION_PROMPT = `
Voce e um parser clinico para anamnese ortopedica por voz.
Responda SOMENTE com JSON valido, sem markdown, sem comentarios e sem texto adicional.

Schema obrigatorio:
{
  "name": string | null,
  "age": number | null,
  "sex": "M" | "F" | null,
  "chiefComplaint": string | null,
  "historyOfPresentIllness": string | null,
  "painLocation": string | null,
  "symptomDuration": string | null,
  "traumaMechanism": string | null,
  "functionalLimitation": string | null,
  "physicalExam": string | null,
  "suspectedDiagnosis": string | null,
  "initialPlan": string | null,
  "notes": string | null
}

Raciocinio sequencial obrigatorio, sem expor o raciocinio:
1. Identifique dados demograficos: nome, idade e sexo.
2. Identifique a queixa principal.
3. Identifique a localizacao anatomica da dor.
4. Identifique tempo de evolucao apenas com contexto clinico claro.
5. Identifique mecanismo de trauma.
6. Identifique achados de exame fisico.
7. Identifique hipotese diagnostica.
8. Identifique plano ou conduta inicial.

Regras clinicas:
- Nao invente dados.
- Se nao tiver certeza, use null.
- Idade deve ser numero inteiro. Exemplo: 36.
- A palavra "anos" associada a idade nunca deve ir para symptomDuration.
- symptomDuration so pode ser preenchido com contexto clinico claro, como "ha 3 dias", "desde ontem" ou "ha 2 semanas".
- Nome so deve ser extraido quando houver padrao de nome proprio ou rotulo claro de nome/paciente.
- Nao use texto livre para preencher campos diretamente.
- Nao substitua julgamento medico.
`.trim();

const clinicalWords = [
  "algia",
  "anamnese",
  "cirurgia",
  "consulta",
  "conduta",
  "diagnostico",
  "diagnóstico",
  "dor",
  "edema",
  "exame",
  "fratura",
  "historia",
  "história",
  "lesao",
  "lesão",
  "limitacao",
  "limitação",
  "luxacao",
  "luxação",
  "plano",
  "queda",
  "queixa",
  "trauma",
];

const cleanText = (value?: string | null) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .replace(/\u00a0/g, " ")
    .trim();

const normalizeForSearch = (value: string) =>
  cleanText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const stripFieldPrefix = (value: string) =>
  cleanText(value)
    .replace(/^[\s:.-]+/, "")
    .replace(/\s+(?:e tambem|e também|associado a|associada a)\s+.*$/i, "")
    .trim();

const trimClinicalClause = (value: string) => {
  const cleaned = stripFieldPrefix(value);
  return cleanText(
    cleaned
      .split(
        /\b(?:queixa principal|motivo da consulta|hist[oó]ria|hda|local da dor|dor no|dor na|dor em|dura[cç][aã]o|mecanismo|exame f[ií]sico|diagn[oó]stico|suspeita|plano|conduta|observa[cç][oõ]es?)\b/i
      )[0]
      ?.replace(/\s+(?:nome|idade|sexo)\s*[:\-].*$/i, "") || cleaned
  );
};

const hasClinicalWord = (value: string) => {
  const normalized = normalizeForSearch(value);
  return clinicalWords.some((word) => normalized.includes(normalizeForSearch(word)));
};

const hasDurationContext = (value: string) =>
  /\b(?:ha|há|desde|evolu(?:cao|ção)|iniciou|inicio|início|com duracao|com duração|durando)\b/i.test(
    value
  );

const sentenceAfterPattern = (text: string, pattern: RegExp) => {
  const match = pattern.exec(text);
  if (!match?.[1]) return "";
  return trimClinicalClause(match[1].split(/[.;\n]/)[0] || match[1]);
};

const candidate = <T extends string | number>(
  value: T | null,
  confidence: number
): FieldCandidate<T> => ({
  value,
  confidence: value == null || String(value).trim() === "" ? 0 : confidence,
});

const extractName = (text: string): FieldCandidate => {
  const labeled = text.match(
    /\b(?:nome(?:\s+do\s+paciente)?|paciente)\s*(?:e|é|eh|chama(?:-se)?|chamado|chamada|:|-)?\s+([A-Za-zÀ-ÿ]+(?:\s+[A-Za-zÀ-ÿ]+){0,4})/i
  );
  const rawName = cleanText(labeled?.[1]);
  if (!rawName) return candidate(null, 0);

  const name = rawName
    .replace(/\b(?:com|de|do|da|dos|das|idade|anos|sexo)\b.*$/i, "")
    .trim();

  if (!name || /\d/.test(name) || name.length > 80 || hasClinicalWord(name)) {
    return candidate(name || null, 0.45);
  }

  const words = name.split(" ").filter(Boolean);
  if (words.length > 4) return candidate(name, 0.55);

  return candidate(name, words.length >= 2 ? 0.86 : 0.68);
};

const extractAge = (text: string): FieldCandidate<number> => {
  const ageMatch = text.match(/\b(?:idade\s*(?:de)?\s*)?(\d{1,3})\s*anos?\b/i);
  const age = ageMatch?.[1] ? Number(ageMatch[1]) : null;
  if (!Number.isInteger(age) || age == null || age <= 0 || age > 120) {
    return candidate(null, 0);
  }

  return candidate(age, 0.94);
};

const extractSex = (text: string): FieldCandidate<"M" | "F"> => {
  const normalized = normalizeForSearch(text);
  if (/\b(sexo masculino|paciente masculino|homem|masculino)\b/.test(normalized)) {
    return candidate("M", 0.88);
  }
  if (/\b(sexo feminino|paciente feminina|mulher|feminino)\b/.test(normalized)) {
    return candidate("F", 0.88);
  }
  return candidate(null, 0);
};

const extractChiefComplaint = (text: string): FieldCandidate => {
  const labeled = sentenceAfterPattern(
    text,
    /\b(?:queixa principal|qp|motivo da consulta|queixa de)\s*[:\-]?\s*([^.;\n]+)/i
  );
  if (labeled) return candidate(labeled, 0.88);

  const referred = sentenceAfterPattern(text, /\b(?:refere|relata|apresenta)\s+([^.;\n]+)/i);
  if (referred) return candidate(referred, 0.68);

  return candidate(null, 0);
};

const extractPainLocation = (text: string): FieldCandidate => {
  const location = sentenceAfterPattern(
    text,
    /\bdor\s+(?:em|no|na|nos|nas|ao nivel de|ao nível de)\s+([^.;,\n]+)/i
  );
  if (!location) return candidate(null, 0);

  const cleaned = location.replace(/\b(?:ha|há|desde|apos|após)\b.*$/i, "").trim();
  return candidate(cleaned || location, cleaned ? 0.84 : 0.62);
};

const extractSymptomDuration = (text: string): FieldCandidate => {
  const explicitDuration = text.match(
    /\b(?:ha|há|desde|evolu(?:cao|ção)\s+de|iniciou\s+ha|iniciou\s+há|com\s+dura(?:cao|ção)\s+de|durando)\s+([^.;,\n]+)/i
  );

  if (!explicitDuration?.[1]) return candidate(null, 0);

  const rawDuration = trimClinicalClause(explicitDuration[1]);
  const duration = rawDuration.match(
    /\b(?:ontem|hoje|anteontem|\d{1,3}\s*(?:horas?|dias?|semanas?|meses?))\b/i
  )?.[0];

  return candidate(duration ? cleanText(duration) : null, duration ? 0.9 : 0);
};

const extractTraumaMechanism = (text: string): FieldCandidate => {
  const labeled = sentenceAfterPattern(
    text,
    /\b(?:mecanismo(?:\s+de|\s+do)?\s+trauma|como aconteceu)\s*[:\-]?\s*([^.;\n]+)/i
  );
  if (labeled) return candidate(labeled, 0.86);

  const trauma = sentenceAfterPattern(
    text,
    /\b(ap[oó]s\s+(?:queda|tor[cç][aã]o|acidente|trauma|colis[aã]o|impacto)[^.;\n]*)/i
  );
  if (trauma) return candidate(trauma, 0.78);

  return candidate(null, 0);
};

const extractFunctionalLimitation = (text: string): FieldCandidate => {
  const limitation = sentenceAfterPattern(
    text,
    /\b(?:limita(?:cao|ção)\s+funcional|dificuldade\s+para|nao\s+consegue|não\s+consegue|perda\s+de\s+funcao|perda\s+de\s+função)\s*[:\-]?\s*([^.;\n]+)/i
  );
  return candidate(limitation || null, limitation ? 0.8 : 0);
};

const extractPhysicalExam = (text: string): FieldCandidate => {
  const exam = sentenceAfterPattern(
    text,
    /\b(?:exame\s+f[ií]sico|ao\s+exame|exame)\s*[:\-]?\s*([^.;\n]+)/i
  );
  return candidate(exam || null, exam ? 0.86 : 0);
};

const extractSuspectedDiagnosis = (text: string): FieldCandidate => {
  const diagnosis = sentenceAfterPattern(
    text,
    /\b(?:diagn[oó]stico\s+suspeito|suspeita\s+diagn[oó]stica|hip[oó]tese\s+diagn[oó]stica|suspeita\s+de|diagn[oó]stico)\s*[:\-]?\s*([^.;\n]+)/i
  );
  return candidate(diagnosis || null, diagnosis ? 0.86 : 0);
};

const extractInitialPlan = (text: string): FieldCandidate => {
  const plan = sentenceAfterPattern(
    text,
    /\b(?:plano\s+inicial|conduta\s+inicial|conduta|plano)\s*[:\-]?\s*([^.;\n]+)/i
  );
  if (plan) return candidate(plan, 0.88);

  const directPlan = sentenceAfterPattern(
    text,
    /\b(solicitar|prescrever|imobilizar|encaminhar|orientar|indicar\s+cirurgia|cirurgia)\s+([^.;\n]+)/i
  );
  return candidate(directPlan || null, directPlan ? 0.72 : 0);
};

const extractNotes = (text: string): FieldCandidate => {
  const notes = sentenceAfterPattern(
    text,
    /\b(?:observa(?:cao|ção|coes|ções)|notas?)\s*[:\-]?\s*([^.;\n]+)/i
  );
  return candidate(notes || null, notes ? 0.78 : 0);
};

const extractHistory = (
  text: string,
  generatedJson: VoicePatientExtractionJson
): FieldCandidate => {
  const labeled = sentenceAfterPattern(
    text,
    /\b(?:hda|hist[oó]ria\s+da\s+doen[cç]a\s+atual|evolu(?:cao|ção)\s+do\s+quadro)\s*[:\-]?\s*([^.;\n]+)/i
  );
  if (labeled) return candidate(labeled, 0.86);

  const structuredParts = [
    generatedJson.chiefComplaint,
    generatedJson.painLocation ? `Local: ${generatedJson.painLocation}` : null,
    generatedJson.symptomDuration ? `Evolucao: ${generatedJson.symptomDuration}` : null,
    generatedJson.traumaMechanism ? `Mecanismo: ${generatedJson.traumaMechanism}` : null,
  ].filter(Boolean);

  if (structuredParts.length >= 2) {
    return candidate(structuredParts.join(". "), 0.64);
  }

  return candidate(null, 0);
};

const createStructuredJson = (transcript: string): {
  json: VoicePatientExtractionJson;
  confidence: Partial<Record<VoicePatientDraftField, number>>;
} => {
  const fields = {
    name: extractName(transcript),
    age: extractAge(transcript),
    sex: extractSex(transcript),
    chiefComplaint: extractChiefComplaint(transcript),
    painLocation: extractPainLocation(transcript),
    symptomDuration: extractSymptomDuration(transcript),
    traumaMechanism: extractTraumaMechanism(transcript),
    functionalLimitation: extractFunctionalLimitation(transcript),
    physicalExam: extractPhysicalExam(transcript),
    suspectedDiagnosis: extractSuspectedDiagnosis(transcript),
    initialPlan: extractInitialPlan(transcript),
    notes: extractNotes(transcript),
  };

  const json: VoicePatientExtractionJson = {
    name: fields.name.value,
    age: fields.age.value,
    sex: fields.sex.value,
    chiefComplaint: fields.chiefComplaint.value,
    historyOfPresentIllness: null,
    painLocation: fields.painLocation.value,
    symptomDuration: fields.symptomDuration.value,
    traumaMechanism: fields.traumaMechanism.value,
    functionalLimitation: fields.functionalLimitation.value,
    physicalExam: fields.physicalExam.value,
    suspectedDiagnosis: fields.suspectedDiagnosis.value,
    initialPlan: fields.initialPlan.value,
    notes: fields.notes.value,
  };

  const history = extractHistory(transcript, json);
  json.historyOfPresentIllness = history.value;

  const confidence: Partial<Record<VoicePatientDraftField, number>> = {
    name: fields.name.confidence,
    age: fields.age.confidence,
    sex: fields.sex.confidence,
    chiefComplaint: fields.chiefComplaint.confidence,
    historyOfPresentIllness: history.confidence,
    painLocation: fields.painLocation.confidence,
    symptomDuration: fields.symptomDuration.confidence,
    traumaMechanism: fields.traumaMechanism.confidence,
    functionalLimitation: fields.functionalLimitation.confidence,
    physicalExam: fields.physicalExam.confidence,
    suspectedDiagnosis: fields.suspectedDiagnosis.confidence,
    initialPlan: fields.initialPlan.confidence,
    notes: fields.notes.confidence,
  };

  return { json, confidence };
};

const appendNote = (notes: string[], field: VoicePatientDraftField, value: string, reason: string) => {
  const cleaned = cleanText(value);
  if (!cleaned) return;
  notes.push(`${field}: ${cleaned} (${reason})`);
};

const validateStructuredJson = (
  transcript: string,
  generatedJson: VoicePatientExtractionJson,
  confidence: Partial<Record<VoicePatientDraftField, number>>
) => {
  const correctedJson: VoicePatientExtractionJson = { ...generatedJson };
  const correctedConfidence = { ...confidence };
  const corrections: VoicePatientParserCorrection[] = [];
  const lowConfidenceFields: VoicePatientDraftField[] = [];
  const noteAdditions: string[] = [];

  if (correctedJson.age != null && (!Number.isInteger(correctedJson.age) || correctedJson.age <= 0)) {
    corrections.push({
      field: "age",
      original: String(correctedJson.age),
      corrected: null,
      reason: "idade invalida",
    });
    correctedJson.age = null;
    correctedConfidence.age = 0;
  }

  if (correctedJson.name && hasClinicalWord(correctedJson.name)) {
    const original = correctedJson.name;
    const cleaned = original
      .split(/\b(?:com|dor|queixa|consulta|trauma|fratura|exame)\b/i)[0]
      .trim();
    correctedJson.name = cleaned && !hasClinicalWord(cleaned) ? cleaned : null;
    correctedConfidence.name = correctedJson.name ? Math.min(correctedConfidence.name ?? 0, 0.62) : 0;
    corrections.push({
      field: "name",
      original,
      corrected: correctedJson.name,
      reason: "nome continha palavras clinicas",
    });
  }

  const symptomDuration = correctedJson.symptomDuration;
  if (symptomDuration && /\banos?\b/i.test(symptomDuration) && !hasDurationContext(transcript)) {
    corrections.push({
      field: "symptomDuration",
      original: symptomDuration,
      corrected: null,
      reason: "anos sem contexto clinico de duracao",
    });
    appendNote(noteAdditions, "symptomDuration", symptomDuration, "movido por baixa confianca/contexto");
    correctedJson.symptomDuration = null;
    correctedConfidence.symptomDuration = 0;
  }

  for (const field of draftFieldKeys) {
    if (field === "notes") continue;
    const value = correctedJson[field];
    const score = correctedConfidence[field] ?? 0;
    if (value != null && cleanText(String(value)) && score < 0.6) {
      lowConfidenceFields.push(field);
      corrections.push({
        field,
        original: String(value),
        corrected: null,
        reason: `confidence ${score.toFixed(2)} abaixo de 0.60`,
      });
      appendNote(noteAdditions, field, String(value), "baixa confianca");
      (correctedJson[field] as null) = null;
    }
  }

  if (noteAdditions.length > 0) {
    correctedJson.notes = cleanText([correctedJson.notes, ...noteAdditions].filter(Boolean).join(" | "));
    correctedConfidence.notes = Math.max(correctedConfidence.notes ?? 0, 0.7);
  }

  return {
    correctedJson,
    confidence: correctedConfidence,
    corrections,
    lowConfidenceFields,
  };
};

const toDraft = (
  json: VoicePatientExtractionJson,
  transcript: string,
  confidence: Partial<Record<VoicePatientDraftField, number>>,
  corrections: VoicePatientParserCorrection[],
  lowConfidenceFields: VoicePatientDraftField[]
): VoicePatientDraft => {
  const draft: VoicePatientDraft = {
    name: json.name ?? undefined,
    age: json.age != null ? String(json.age) : undefined,
    sex: json.sex ?? undefined,
    chiefComplaint: json.chiefComplaint ?? undefined,
    historyOfPresentIllness: json.historyOfPresentIllness ?? undefined,
    painLocation: json.painLocation ?? undefined,
    symptomDuration: json.symptomDuration ?? undefined,
    traumaMechanism: json.traumaMechanism ?? undefined,
    functionalLimitation: json.functionalLimitation ?? undefined,
    physicalExam: json.physicalExam ?? undefined,
    suspectedDiagnosis: json.suspectedDiagnosis ?? undefined,
    initialPlan: json.initialPlan ?? undefined,
    notes: json.notes ?? undefined,
    transcript,
    structuredJson: json,
    confidence,
    lowConfidenceFields,
    parserCorrections: corrections,
  };

  draft.autofilledFields = draftFieldKeys.filter((field) => {
    const value = draft[field];
    return typeof value === "string" ? value.trim().length > 0 : value != null;
  });

  return draft;
};

export async function transcribePatientAudio(
  audio: Blob,
  options: TranscriptSource = {}
): Promise<string> {
  const fallbackTranscript = cleanText(options.fallbackTranscript);

  if (fallbackTranscript) {
    return fallbackTranscript;
  }

  // Fallback seguro ate a integracao com Firebase Function estar pronta.
  // Nao inventa conteudo clinico: apenas preserva a ausencia de transcricao.
  if (options.useMock ?? true) {
    return "";
  }

  void audio;
  void options.language;
  return "";
}

export async function extractPatientDataFromTranscript(
  transcript: string
): Promise<VoicePatientDraft> {
  const normalizedTranscript = cleanText(transcript);

  if (!normalizedTranscript) {
    return {};
  }

  const { json: generatedJson, confidence } = createStructuredJson(normalizedTranscript);
  const { correctedJson, corrections, confidence: correctedConfidence, lowConfidenceFields } =
    validateStructuredJson(normalizedTranscript, generatedJson, confidence);

  console.info("[PatientVoiceIntake][transcript]", normalizedTranscript);
  console.info("[PatientVoiceIntake][json]", JSON.stringify(generatedJson));
  console.info("[PatientVoiceIntake][confidence]", correctedConfidence);
  if (corrections.length > 0) {
    console.warn("[PatientVoiceIntake][corrections]", corrections);
  }

  return toDraft(
    correctedJson,
    normalizedTranscript,
    correctedConfidence,
    corrections,
    lowConfidenceFields
  );
}

const mergeTextField = (
  current: string | undefined,
  incoming: string | undefined
) => {
  const next = cleanText(incoming);
  if (!next) return current;
  return next;
};

export function applyVoicePatientDraftToForm(
  current: Partial<Patient>,
  draft: VoicePatientDraft
): Partial<Patient> {
  return {
    ...current,
    name: mergeTextField(current.name, draft.name),
    age: mergeTextField(current.age, draft.age),
    sex: draft.sex ?? current.sex,
    chiefComplaint: mergeTextField(current.chiefComplaint, draft.chiefComplaint),
    historyOfPresentIllness: mergeTextField(
      current.historyOfPresentIllness,
      draft.historyOfPresentIllness
    ),
    painLocation: mergeTextField(current.painLocation, draft.painLocation),
    symptomDuration: mergeTextField(current.symptomDuration, draft.symptomDuration),
    traumaMechanism: mergeTextField(current.traumaMechanism, draft.traumaMechanism),
    functionalLimitation: mergeTextField(
      current.functionalLimitation,
      draft.functionalLimitation
    ),
    physicalExam: mergeTextField(current.physicalExam, draft.physicalExam),
    suspectedDiagnosis: mergeTextField(
      current.suspectedDiagnosis,
      draft.suspectedDiagnosis
    ),
    initialPlan: mergeTextField(current.initialPlan, draft.initialPlan),
    notes: mergeTextField(current.notes, draft.notes),
  };
}
